import { apiClient } from '@/api/generated/client'
import type { AccountUser } from '@/api/generated/types'
import { makeAutoObservable, runInAction } from 'mobx'

import { RequestError, onSignedOut } from '@/api/http'
import { hasSession, signOutOfIntch } from '@/api/intchAuth'
import { flushAccountPush } from '@/utils/account/accountPush'
import { markSessionLost, markSignedOut, syncAfterSignIn } from '@/utils/account/accountSync'
import { setAnalyticsUser, track } from '@/utils/analytics'

/**
 * Вошёл ли человек через Intch — второй глобальный стор рядом с serviceStore, потому что ответ нужен
 * каждому закрытому экрану сразу, а не после монтирования своего стора.
 *
 * `unknown` — ещё не спросили: закрытый экран в это время не рисует ничего (ни себя, ни вход), иначе
 * вошедший человек видел бы вспышку экрана входа на каждой загрузке. `authenticated` наступает ПОСЛЕ
 * того, как копия кабинета в браузере заполнена с сервера (accountSync.ts): сторы кабинета читают её
 * в конструкторе, и открыть их раньше значило бы показать пустой кабинет.
 */
export type AuthStatus = 'unknown' | 'anonymous' | 'authenticated'

class AuthStore {
  status: AuthStatus = 'unknown'
  user: AccountUser | null = null
  /** Монолит не ответил на проверку токена — не «не вошёл», выходить из аккаунта не нужно. */
  unavailable = false
  private started = false

  constructor() {
    makeAutoObservable<AuthStore, 'started'>(this, { started: false })
    onSignedOut(() => this.lose())
  }

  get isAuthenticated() {
    return this.status === 'authenticated'
  }

  get email() {
    return this.user?.email ?? null
  }

  /** Спросить один раз за загрузку. Без cookie — сразу аноним, без запроса. */
  init = () => {
    if (this.started) return
    this.started = true
    if (!hasSession()) {
      this.status = 'anonymous'
      return
    }
    void this.load('silent')
  }

  /**
   * Кто это и заполнить кабинет. `entry` — откуда пришли, для аналитики: код в онбординге, код на
   * экране входа или молча по cookie, оставшимся от intch.org или прошлого визита.
   */
  load = async (entry: 'onboard' | 'login' | 'signup' | 'silent', code?: { newAccount: boolean }): Promise<boolean> => {
    this.unavailable = false
    let user: AccountUser
    try {
      user = (await apiClient.me.me()).user
    } catch (error) {
      runInAction(() => {
        if (error instanceof RequestError && error.status === 503) {
          this.unavailable = true
          this.status = 'unknown'
        } else {
          this.status = 'anonymous'
        }
      })
      return false
    }

    try {
      await syncAfterSignIn(user)
    } catch {
      /* Кабинет не заполнился с сервера — человек всё равно вошёл; копия догонит при следующем входе. */
    }
    setAnalyticsUser(user.intch_user_id)
    /* `new_account` — монолит завёл аккаунт по этому коду (`first_login`): регистрация, а не вход. */
    if (entry !== 'silent') track('auth_signed_in', { entry, ...(code ? { new_account: code.newAccount } : {}) })
    runInAction(() => {
      this.user = user
      this.status = 'authenticated'
    })
    return true
  }

  /** Повторить после «монолит не ответил». */
  retry = () => {
    void this.load('silent')
  }

  /** Имя и роль из онбординга — чтобы кабинет показал их, не дожидаясь следующей загрузки. */
  setProfile = (input: { name?: string; role?: string }) => {
    if (!this.user) return
    this.user = { ...this.user, name: input.name || this.user.name, role: input.role || this.user.role }
  }

  logout = async () => {
    await flushAccountPush()
    await signOutOfIntch()
    track('auth_signed_out')
    this.forget()
  }

  private forget = () => {
    markSignedOut()
    this.lose()
  }

  /** Вход кончился без «Выйти»: человек уходит на вход, копия кабинета остаётся (markSessionLost). */
  private lose = () => {
    markSessionLost()
    setAnalyticsUser(null)
    this.user = null
    this.status = 'anonymous'
  }
}

export const authStore = new AuthStore()
